import { SiteUrls } from '@tableau/shared-urls';

import { Messages } from '~src/codegen/Localize';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { isMetricsEnabled } from '~src/metrics/util/MetricUtils';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { isSimulationEnabled } from '~src/simulation/util/SimulationUtils';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';

import { ContentSuggestionDataItem, ContentType } from './Types';

export type ContentDefinition = {
    getName: () => string;
    icon: string;
    getUrl: (urls: SiteUrls, item: ContentSuggestionDataItem) => string;
    explorableItemType: ExplorableItemType;
    shouldBeAvailable: () => boolean;
};

export const ContentDefinitions: { [type in ContentType]: ContentDefinition } = {
    [ContentType.view]: {
        getName: () => Messages.views(),
        icon: 'view_18px-large.svg',
        getUrl: (urls, item) => urls.viewUrl(item.content.path),
        explorableItemType: 'view',
        shouldBeAvailable: () => true
    },
    [ContentType.metric]: {
        getName: () => Messages.metrics(),
        icon: 'metrics_18px-large.svg',
        getUrl: (urls, item) => urls.metricUrl(item.content.id.toString()),
        explorableItemType: 'metric',
        shouldBeAvailable: () => isMetricsEnabled()
    },
    [ContentType.lens]: {
        getName: () => Messages.lenses(),
        icon: 'lens_36px-jumbo.svg',
        getUrl: (urls, item) => urls.lensUrl(item.content.id.toString()),
        explorableItemType: 'lens',
        shouldBeAvailable: () => true
    },
    [ContentType.unifieddatasource]: {
        getName: () => Messages.datasources(),
        icon: 'datasource_18px-large.svg',
        getUrl: (urls, item) => urls.datasourceUrl(item.content.id.toString()),
        explorableItemType: 'datasource',
        shouldBeAvailable: () => true
    },
    [ContentType.workbook]: {
        getName: () => Messages.workbooks(),
        icon: 'workbook_18px-large.svg',
        getUrl: (urls, item) => urls.workbookUrl(item.content.id.toString()),
        explorableItemType: 'workbook',
        shouldBeAvailable: () => true
    },
    [ContentType.collection]: {
        getName: () => Messages.collections(),
        icon: 'collection_18px-large.svg',
        getUrl: (urls, item) => urls.collectionUrl(item.content.luid.toString()),
        explorableItemType: 'collection',
        shouldBeAvailable: () => CollectionUtils.checkCollectionSiteSetting()
    },
    [ContentType.project]: {
        getName: () => Messages.projects(),
        icon: 'project_content_type_small.svg',
        getUrl: (urls, item) => urls.projectUrl(item.content.id.toString()),
        explorableItemType: 'project',
        shouldBeAvailable: () => true
    },
    [ContentType.flow]: {
        getName: () => Messages.flows(),
        icon: 'flow_18px-large.svg',
        getUrl: (urls, item) => urls.flowUrl(item.content.id.toString()),
        explorableItemType: 'flow',
        shouldBeAvailable: () => true
    },
    [ContentType.datarole]: {
        getName: () => Messages.dataRoles(),
        icon: 'data_role_content_type_small.svg',
        getUrl: (urls, item) => urls.dataRoleUrl(item.content.id.toString()),
        explorableItemType: 'dataRole',
        shouldBeAvailable: () => true
    },
    [ContentType.table]: {
        getName: () => Messages.tables(),
        icon: 'table_18px-large.svg',
        getUrl: (urls, item) => urls.catalogTableUrl(item.content.id.toString()),
        explorableItemType: 'tableWithCrass',
        shouldBeAvailable: () => {
            const sessionManagementService: SessionManagementService = getSessionManagementServiceInstance();
            return sessionManagementService.catalogingOnSiteEnabled();
        }
    },
    [ContentType.database]: {
        getName: () => Messages.databasesAndFiles(),
        icon: 'external_server_connection_type_small.svg',
        getUrl: (urls, item) => urls.catalogDatabaseUrl(item.content.id.toString()),
        explorableItemType: 'databaseWithCrass',
        shouldBeAvailable: () => {
            const sessionManagementService: SessionManagementService = getSessionManagementServiceInstance();
            return sessionManagementService.catalogingOnSiteEnabled();
        }
    },
    [ContentType.virtualconnection]: {
        getName: () => Messages.publishedConnections(),
        icon: 'published_connection_table_medium.svg',
        getUrl: (urls, item) => urls.virtualConnectionUrl(item.content.id.toString()),
        explorableItemType: 'virtualConnection',
        shouldBeAvailable: () => true
    },
    [ContentType.simulation]: {
        getName: () => Messages.simulations(),
        icon: 'simulation_18px-large.svg',
        getUrl: (urls, item) => urls.simulationUrl(item.content.id.toString()),
        explorableItemType: 'simulation',
        shouldBeAvailable: () => isSimulationEnabled()
    }
};